import { useCallback, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from 'src/firebase/firebaseConfig';
import { ClientField, ClientFormInstance } from 'shared/types/appTypes';
import { useNotifications } from '@toolpad/core';

export interface ClientFormValues {
  fields: Record<string, any>;
}

export interface UseClientFormReturn {
  form: UseFormReturn<ClientFormValues>;
  fields: ClientField[];
  isLoading: boolean;
  isSaving: boolean;
  saveField: (fieldIndex: number) => Promise<void>;
  getFieldValue: (fieldIndex: number) => any;
  setFieldValue: (fieldIndex: number, value: any) => void;
}

export const useClientForm = (eventId: string | undefined): UseClientFormReturn => {
  const [fields, setFields] = useState<ClientField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const notifications = useNotifications();

  const form = useForm<ClientFormValues>({
    defaultValues: {
      fields: {},
    },
  });

  // Load fields from Firebase
  useEffect(() => {
    const loadFields = async () => {
      if (!eventId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const docRef = doc(db, 'clientForms', eventId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as ClientFormInstance;

          // Record that the client opened the form
          try {
            await updateDoc(docRef, {
              clientLastOpenedAt: Timestamp.now(),
            });
          } catch (e) {
            console.error('Error recording clientLastOpenedAt:', e);
          }

          const loadedFields = data.fields || [];
          const formValues: Record<string, any> = {};

          loadedFields.forEach((field, index) => {
            // Video fields don't have a value property
            if (field.type !== 'video' && 'value' in field) {
              formValues[index] = field.value;
            }
          });

          setFields(loadedFields);
          form.reset({ fields: formValues });
        } else {
          setFields([]);
        }
      } catch (error) {
        console.error('Error loading client form fields:', error);
        notifications.show('Failed to load form fields', { severity: 'error', autoHideDuration: 3000 });
      } finally {
        setIsLoading(false);
      }
    };

    loadFields();
  }, [eventId, form, notifications]);

  // Save a single field to Firebase
  const saveField = useCallback(async (fieldIndex: number) => {
    if (!eventId || !fields[fieldIndex]) return;

    const field = fields[fieldIndex];

    // Prevent saving finalized fields
    if (field.type !== 'video' && 'finalized' in field && field.finalized) return;

    const value = form.getValues(`fields.${fieldIndex}`);

    try {
      setIsSaving(true);
      
      // Update the specific field in the array
      const updatedFields = [...fields];
      const currentTimestamp = Timestamp.now();
      const currentUserEmail = auth.currentUser?.email || '';
      
      // Video fields don't have a value property to update
      if (field.type === 'video') {
        updatedFields[fieldIndex] = {
          ...field,
          modifiedAt: currentTimestamp,
          modifiedBy: currentUserEmail,
        };
      } else {
        // Get the previous value for edit history
        const previousValue = 'value' in field ? field.value : null;
        
        // Build edit history entry if value actually changed
        let updatedEditHistory = field.editHistory || [];
        if (JSON.stringify(previousValue) !== JSON.stringify(value)) {
          updatedEditHistory = [
            ...updatedEditHistory,
            {
              modifiedAt: currentTimestamp,
              modifiedBy: currentUserEmail,
              previousValue,
            },
          ];
        }
        
        updatedFields[fieldIndex] = {
          ...field,
          value,
          modifiedAt: currentTimestamp,
          modifiedBy: currentUserEmail,
          editHistory: updatedEditHistory,
          // Mark confirmed if this is an auto-populated field
          ...('autoPopulatedValue' in field && field.autoPopulatedValue !== undefined
            ? { clientConfirmed: true }
            : {}),
        } as ClientField;
      }

      const docRef = doc(db, 'clientForms', eventId);
      await updateDoc(docRef, {
        fields: updatedFields,
        updatedAt: Timestamp.now(),
        updatedBy: auth.currentUser?.email || '',
      });

      // Update local state
      setFields(updatedFields);

    } catch (error) {
      console.error('Error saving field:', error);
      notifications.show('Failed to save field', { severity: 'error', autoHideDuration: 3000 });
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [eventId, fields, form, notifications]);

  const getFieldValue = useCallback((fieldIndex: number) => {
    if (!fields[fieldIndex]) return undefined;
    const val = form.watch(`fields.${fieldIndex}`);
    return val;
  }, [fields, form]);

  const setFieldValue = useCallback((fieldIndex: number, value: any) => {
    if (!fields[fieldIndex]) return;
    form.setValue(`fields.${fieldIndex}`, value);
  }, [fields, form]);

  return {
    form,
    fields,
    isLoading,
    isSaving,
    saveField,
    getFieldValue,
    setFieldValue,
  };
};

export default useClientForm;
